import gql from 'graphql-tag';

export type {
    ForecastingResultsQuery,
    ForecastingResultsQueryVariables,
    ForecastingResultsQuery_forecastingResults,
    ForecastingResultsQuery_forecastingResults_data,
    ForecastingResultsQuery_forecastingResults_data_map,
    ForecastingResultsQuery_forecastingResults_data_map_data
} from './__generated__/ForecastingResultsQuery';

export type {
    SummaryStatisticsQuery,
    SummaryStatisticsQueryVariables,
    SummaryStatisticsQuery_forecastingResults,
    SummaryStatisticsQuery_forecastingResults_data,
    SummaryStatisticsQuery_forecastingResults_data_summary
} from './__generated__/SummaryStatisticsQuery';

export type {
    PrimaryForecastingMutation,
    PrimaryForecastingMutationVariables,
    PrimaryForecastingMutation_primaryForecasting
} from './__generated__/PrimaryForecastingMutation';

export type {
    ExportDataQuery,
    ExportDataQueryVariables,
    ExportDataQuery_forecastingResults,
    ExportDataQuery_forecastingResults_data,
    ExportDataQuery_forecastingResults_data_export
} from './__generated__/ExportDataQuery';

export type {
  AutoCompleteQuery_populateAutoComplete,
  AutoCompleteQuery,
  AutoCompleteQueryVariables
} from './__generated__/AutoCompleteQuery';


export type {
    PollForecastingQuery,
    PollForecastingQueryVariables,
    PollForecastingQuery_forecastingResults
} from './__generated__/PollForecastingQuery';

export const PRIMARY_FORECASTING_MUTATION = gql`
mutation PrimaryForecastingMutation(
    $ssp: [String!]!
    $campaignStart: String = ""
    $campaignEnd: String = ""
    $hourOfWeeks: [Int!]
    $publisher: [String!]
    $country: [String!]
    $region: [String!]
    $metro: [String!]
    $city: [String!]
    $zip: [String!]
    $venueTypes: [String!]
    $creativeTypes: [CreativeType!]
    $audienceId: String
    $includeAudienceTier1: Boolean
    $includeAudienceTier2: Boolean
    $includeAudienceTier3: Boolean
    $includeAudienceTier4: Boolean
    $winRate: Float
    $pointsOfInterest: [PointOfInterestInput!]
    $polygonsOfInterest: [[PointOfInterestInput!]!]
    $poiType: POIType
  ){
    primaryForecasting(ssp: $ssp,
        campaignStart: $campaignStart, 
        campaignEnd: $campaignEnd,
        hourOfWeeks: $hourOfWeeks,
        publisher: $publisher,
        country: $country, 
        region: $region, 
        metro: $metro, 
        city: $city, 
        zip: $zip,
        venueTypes: $venueTypes,
        creativeTypes: $creativeTypes,
        audienceId: $audienceId,
        includeAudienceTier1: $includeAudienceTier1,
        includeAudienceTier2: $includeAudienceTier2,
        includeAudienceTier3: $includeAudienceTier3,
        includeAudienceTier4: $includeAudienceTier4,
        winRate: $winRate,
        pointsOfInterest: $pointsOfInterest,
        polygonsOfInterest: $polygonsOfInterest,
        poiType: $poiType) {
      forecastingId
    }
  }
`;

export const CANCEL_FORECASTING_MUTATION = gql`
mutation CancelForecastingMutation(
    $forecastingId: String!
  ){
    cancelForecasting(forecastingId: $forecastingId)
  }
`;

const FORECASTING_RESULT_QUERY_BUILDER = (mapData: string) => `
query ForecastingResultsQuery (
    $neLat: Float!
    $neLng: Float!
    $swLat: Float!
    $swLng: Float!
    $forecastingId: String!
  ){
    forecastingResults(forecastingId: $forecastingId) {
      data {
        status
        map(viewPort: {
          maxLatitude: $neLat,
          maxLongitude: $neLng,
          minLatitude: $swLat,
          minLongitude: $swLng,
        }) {
          h3
          data {
            ${mapData}       
          }
        }
      }
    }
  }
`

export const FORECASTING_RESULTS_QUERY = (mapData: string) => gql(FORECASTING_RESULT_QUERY_BUILDER(mapData));

export const FORECASTING_SCREEN_RESULTS_QUERY = gql`
query ForecastingScreenResultsQuery (
    $neLat: Float!
    $neLng: Float!
    $swLat: Float!
    $swLng: Float!
    $forecastingId: String!
  ){
    forecastingResults(forecastingId: $forecastingId) {
      data {
        status
        screens(viewPort: {
          maxLatitude: $neLat,
          maxLongitude: $neLng,
          minLatitude: $swLat,
          minLongitude: $swLng,
        }) {
          screenId
          latitude
          longitude
          publisher
          venueType
          numberOfImpressions
          numberOfAudienceImpressions
          potentialSpend
          averageCpm
        }
      }
    }
  }
`;

export const SUMMARY_STATISTICS_QUERY = gql`
query SummaryStatisticsQuery(
    $forecastingId: String = "default",
    $url: String = null
  ){
    forecastingResults(forecastingId: $forecastingId, url: $url) {
      data {
        status
        summary {
          numberOfScreens,
          potentialSpend,
          averageCpm,
          numberOfImpressions,
          numberOfAudienceImpressions,
          minLat,
          minLon,
          maxLat,
          maxLon
        }
      }
    }
  }
`;

export const EXPORT_DATA_QUERY = gql`
query ExportDataQuery(
    $forecastingId: String = "default"
    ){
    forecastingResults(forecastingId: $forecastingId) {
        data {
          status
          export {
             row
           }
        }
    }
  }
`;

export const POLL_FORECASTING_QUERY = gql`
query PollForecastingQuery(
    $forecastingId: String!,
    $longPoll: Boolean!,
){
    forecastingResults(forecastingId: $forecastingId, longPoll: $longPoll) {
        data {
            status
        }
    }
}
`;

export const AUTOCOMPLETE_QUERY = gql`
query AutoCompleteQuery(
    $type: SearchType!
    $ssp: [String!]!
    $searchTerm: String = ""
    $limit: Int = 100
    $publisher: [String!]
    $venueType: [String!]
    $country: [String!]
    $region: [String!]
    $metro: [String!]
    $city: [String!]
    $zip: [String!]
  ){
    populateAutoComplete(type: $type,
      ssp: $ssp,
      searchTerm: $searchTerm,
      limit: $limit,
      publisher: $publisher,
      venueType: $venueType,
      country: $country, 
      region: $region
      metro: $metro,
      city: $city,
      zip: $zip,
    ) {
        entries {
            label
            value
        }
    }
  }
`;

export const FILTER_VALIDATION_QUERY = gql`
query FilterValidationQuery(
    $type: SearchType!
    $searchTerms: [String!]
    $ssp: [String!]!
    $publisher: [String!]
    $venueType: [String!]
    $country: [String!]
    $region: [String!]
    $metro: [String!]
    $city: [String!]
    $zip: [String!]
  ){
    filterDependencyValidation(type: $type,
      searchTerms: $searchTerms,
      ssp: $ssp,
      publisher: $publisher,
      venueType: $venueType,
      country: $country,
      region: $region
      metro: $metro,
      city: $city,
      zip: $zip,
    )
  }
`;

export const CAMPAIGN_RESULTS_QUERY = gql`
query CampaignVisResultsQuery (
    $campaignId: String!
  ){
    campaignVisualisationResults(campaignId: $campaignId) {
        campaignId
        supplyVendor
        bidFeedbackId
        deviceType
        logEntryTime
    }
  }
`

export const OVERALL_CAMPAIGN_RESULTS_QUERY = gql`
query CampaignVisOverallResultsQuery (
    $campaignId: String!
  ){
    overallCampaignVisualisationResults(campaignId: $campaignId) {
        impressions,
        minLat,
        maxLat,
        minLon,
        maxLon
    }
  }
`

export const CAMPAIGN_VIS_MAP_QUERY = gql`
query CampaignVisMap (
    $neLat: Float!
    $neLng: Float!
    $swLat: Float!
    $swLng: Float!
    $campaignId: String!
  ){
    campaignVisMap(campaignId: $campaignId, viewPort: {
              maxLatitude: $neLat,
              maxLongitude: $neLng,
              minLatitude: $swLat,
              minLongitude: $swLng,
        }) {
          h3
          data {
            impressions  
            minLat
            maxLat
            minLon
            maxLon   
            }
          }
        }
`
